import { FieldNames } from '@/constants/FieldNames';
import IMLSPropertyItem from './IMLSPropertyItem';
import CompAddressUtils from '../comp/CompAddressUtils';
import IAddress1UnitNoZipCode, { newAddress1UnitNoZipCode } from '../common/IAddress1UnitNoZipCode';
import Address1UnitNoZipCodeUtils from '../common/Address1UnitNoZipCodeUtils';
import ILookup, { newLookupWithStringsNumbersBooleans } from '../common/ILookup';
import ICompAddress, { newCompAddress } from '../CompAddress';
import IFeedData from '../comp/IFeedData';

export default {
    getAddress(propertyItem: IMLSPropertyItem): ICompAddress {
        const address = newCompAddress();

        let foundCount = 0;
        for (const mlsData of propertyItem.fields) {
            switch (mlsData.formFieldName) {
                case FieldNames.Address1:
                    address.address1 = mlsData.formData ? '' + mlsData.formData : '';
                    foundCount++;
                    break;
                case FieldNames.Address2:
                    address.address2 = mlsData.formData ? '' + mlsData.formData : '';
                    foundCount++;
                    break;
                case FieldNames.UnitNo:
                    address.unitNumber = mlsData.formData ? '' + mlsData.formData : '';
                    foundCount++;
                    break;
                case FieldNames.City:
                    address.city = mlsData.formData ? '' + mlsData.formData : '';
                    foundCount++;
                    break;
                case FieldNames.State:
                    address.state = mlsData.formData ? '' + mlsData.formData : '';
                    foundCount++;
                    break;
                case FieldNames.ZipCode:
                    address.zipCode = mlsData.formData ? '' + mlsData.formData : '';
                    foundCount++;
                    break;
            }

            if (foundCount >= 6) {
                break;
            }
        }

        return address;
    },

    combineAddress1AndUnitNo(mlsPropertyItem: IMLSPropertyItem): string {
        const address1 = getAddress1(mlsPropertyItem);
        const unitNumber = getUnitNumber(mlsPropertyItem);
        const result = CompAddressUtils.combineAddressAndUnitNo(address1, unitNumber);
        return result;
    },

    /**
     * Get an address1, unit number and zip code object from the given MLS property item.
     * @param propertyItem
     * @returns
     */
    getAddress1UnitNoAndZipCode(propertyItem: IMLSPropertyItem): IAddress1UnitNoZipCode {
        const result = newAddress1UnitNoZipCode();

        let foundCount = 0;
        for (const mlsData of propertyItem.fields) {
            switch (mlsData.formFieldName) {
                case FieldNames.Address1:
                    result.address1 = mlsData.formData ? '' + mlsData.formData : '';
                    foundCount++;
                    break;
                case FieldNames.UnitNo:
                    result.unitNumber = mlsData.formData ? '' + mlsData.formData : '';
                    foundCount++;
                    break;
                case FieldNames.ZipCode:
                    result.zipCode = mlsData.formData ? '' + mlsData.formData : '';
                    foundCount++;
                    break;
            }

            if (foundCount >= 3) {
                break;
            }
        }

        return result;
    },

    getAddress1UnitNoAndZipCode36(feedData: IFeedData): IAddress1UnitNoZipCode {
        const result = newAddress1UnitNoZipCode();

        if (feedData?.address?.address1) {
            result.address1 = feedData.address.address1 ?? '';
            result.unitNumber = feedData.address.unitNumber ?? '';
            result.zipCode = feedData.address.zipCode ?? '';
        } else if (feedData?.sourceData) {
            // ConvertMLSMapperDataToUAD36 doesn't always include a pre-built address object - derive it from the flat sourceData fields.
            result.address1 = feedData.sourceData['AD_ADDR1'] ? String(feedData.sourceData['AD_ADDR1']) : '';
            result.unitNumber = feedData.sourceData['DB_UNITNO'] ? String(feedData.sourceData['DB_UNITNO']) : '';
            result.zipCode = feedData.sourceData['DB_ZIP'] ? String(feedData.sourceData['DB_ZIP']) : '';
        }

        return result;
    },

    /**
     * Compare the given property item's address1, unit no & zip code against the given
     * criteria.
     * @param propertyItem
     * @param criteria
     * @returns true on all fields are equal, false otherwise
     */
    matches(propertyItem: IMLSPropertyItem, criteria: IAddress1UnitNoZipCode): boolean {
        const propertyCriteria = this.getAddress1UnitNoAndZipCode(propertyItem);
        const result = Address1UnitNoZipCodeUtils.matches(propertyCriteria, criteria);
        return result;
    },

    getFactSheetDatas(mlsPropertyItem: IMLSPropertyItem): ILookup<string | number | boolean> {
        const factSheetDatas = newLookupWithStringsNumbersBooleans();

        if (mlsPropertyItem) {
            const fieldIdx = mlsPropertyItem.fields.findIndex(x => x.formFieldName === FieldNames.DataFactSheet);
            if (fieldIdx >= 0) {
                const mlsData = mlsPropertyItem.fields[fieldIdx];
                if (mlsData.propertySource) {
                    for (const propertySource of mlsData.propertySource) {
                        if (propertySource.headers) {
                            factSheetDatas[propertySource.headers] = propertySource.val ?? '';
                        }
                    }
                }
            }
        }

        return factSheetDatas;
    }
};

function getAddress1(mlsPropertyItem: IMLSPropertyItem): string {
    const formData = getFormData(mlsPropertyItem, FieldNames.Address1);
    if (formData) {
        return '' + formData;
    }
    return '';
}

function getUnitNumber(mlsPropertyItem: IMLSPropertyItem): string {
    const formData = getFormData(mlsPropertyItem, FieldNames.UnitNo);
    if (formData) {
        return '' + formData;
    }
    return '';
}

function getFormData(mlsPropertyItem: IMLSPropertyItem, fieldName: FieldNames): string | number | boolean | null {
    const fieldIdx = mlsPropertyItem.fields.findIndex(x => x.formFieldName === fieldName);
    if (fieldIdx >= 0) {
        const formData = mlsPropertyItem.fields[fieldIdx].formData;
        return formData;
    }
    return null;
}
